import {Alert, Anchor, Badge, Button, Card, Group, Stack, Table, Text, Title} from '@mantine/core';
import {useQuery} from '@tanstack/react-query';
import {fetchEventPipelineHealth} from '../services/EventPipelineHealthService';
import {
    busBadge,
    busConsoleUrl,
    LayerBadge,
    ruleBadge,
    ruleConsoleUrl,
    stateMachineBadge,
    stateMachineConsoleUrl,
} from '../utils/eventPipelineHealth';
import {extractErrorMessage, StripePartnerBusHealth} from '../types';

export const PIPELINE_SCOPE_LABEL = 'ingress → state machine execution';

const ToneBadge = ({badge}: { badge: LayerBadge }) => (
    <Badge color={badge.tone} variant="light" style={{textTransform: 'none'}}>{badge.label}</Badge>
);

// The partner bus id is the useful part of aws.partner/stripe.com/<id>.
const shortBusName = (name: string) => name.split('/').pop() ?? name;

interface VerifyEventsPanelProps {
    windowStart: Date;
}

// FNP-412: after a Payment Flow Tester flow, did Stripe's events reach the
// partner bus, match the rule and run the state machine? Read-only, refreshed
// on demand only: CloudWatch metrics lag a few minutes, so there is no polling.
export const VerifyEventsPanel = ({windowStart}: VerifyEventsPanelProps) => {
    const query = useQuery({
        queryKey: ['eventPipelineHealth', windowStart.toISOString()],
        queryFn: () => fetchEventPipelineHealth(windowStart, new Date()),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false,
    });

    const result = query.data;

    return (
        <Card withBorder padding="md" aria-label="Verify Events">
            <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                    <div>
                        <Title order={5}>Verify Events</Title>
                        <Text size="xs" c="dimmed">Scope: {PIPELINE_SCOPE_LABEL}</Text>
                        <Text size="xs" c="dimmed">
                            Since flow start, {windowStart.toLocaleTimeString()}. Metrics lag a few minutes; refresh to re-check.
                        </Text>
                    </div>
                    <Button size="xs" variant="light" onClick={() => query.refetch()} loading={query.isFetching}>
                        Refresh
                    </Button>
                </Group>

                {query.isError && (
                    <Alert color="red" title="Could not read event pipeline health">
                        {extractErrorMessage(query.error)}
                    </Alert>
                )}

                {result?.status === 'disabled' && (
                    <Alert color="gray">Event pipeline health is disabled in this stage.</Alert>
                )}

                {result?.status === 'ok' && (
                    <Table verticalSpacing="xs" fz="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Layer</Table.Th>
                                <Table.Th>Counts</Table.Th>
                                <Table.Th>Status</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {result.health.buses.map(bus => (
                                <BusRows key={bus.eventBusName} bus={bus}/>
                            ))}
                            <Table.Tr>
                                <Table.Td>
                                    <Anchor href={stateMachineConsoleUrl(result.health.stateMachine)} target="_blank" size="sm">
                                        State machine
                                    </Anchor>
                                </Table.Td>
                                <Table.Td>
                                    {result.health.stateMachine.executionsStarted} started,{' '}
                                    {result.health.stateMachine.executionsSucceeded} succeeded,{' '}
                                    {result.health.stateMachine.executionsFailed} failed,{' '}
                                    {result.health.stateMachine.executionsTimedOut} timed out,{' '}
                                    {result.health.stateMachine.executionsAborted} aborted
                                </Table.Td>
                                <Table.Td><ToneBadge badge={stateMachineBadge(result.health.stateMachine)}/></Table.Td>
                            </Table.Tr>
                        </Table.Tbody>
                    </Table>
                )}
            </Stack>
        </Card>
    );
};

const BusRows = ({bus}: { bus: StripePartnerBusHealth }) => (
    <>
        <Table.Tr>
            <Table.Td>
                <Anchor href={busConsoleUrl(bus)} target="_blank" size="sm">
                    Partner bus {shortBusName(bus.eventBusName)}
                </Anchor>
            </Table.Td>
            <Table.Td>{bus.eventsReceived} received</Table.Td>
            <Table.Td><ToneBadge badge={busBadge(bus)}/></Table.Td>
        </Table.Tr>
        {bus.observability === 'NO_RULES' && (
            <Table.Tr>
                <Table.Td pl="lg"><Text size="sm" c="dimmed">Rule, state machine</Text></Table.Td>
                <Table.Td colSpan={2}>
                    <Text size="sm" c="dimmed">Not applicable: this bus has no rules, so nothing is routed from it.</Text>
                </Table.Td>
            </Table.Tr>
        )}
        {bus.rules.map(rule => (
            <Table.Tr key={rule.ruleArn}>
                <Table.Td pl="lg">
                    <Anchor href={ruleConsoleUrl(bus, rule)} target="_blank" size="sm">
                        Rule{rule.startsStateMachine ? ' (starts state machine)' : ''}
                    </Anchor>
                </Table.Td>
                <Table.Td>
                    {rule.matchedEvents} matched, {rule.triggeredRules} triggered,{' '}
                    {rule.invocations} invocations, {rule.failedInvocations} failed
                    {rule.deadLetterInvocations != null ? `, ${rule.deadLetterInvocations} dead-lettered` : ''}
                </Table.Td>
                <Table.Td><ToneBadge badge={ruleBadge(rule, bus.eventsReceived)}/></Table.Td>
            </Table.Tr>
        ))}
    </>
);
